import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { ResizableBox } from "react-resizable";

import { Input } from "../../../Input";
import { placeholder } from "../../consts";

import "react-resizable/css/styles.css";

const minImageWidth = 40; // pixels;

// restriction imposed by previewer CSS
const maxImageWidth = 620; // pixels

// padding around image for resize handles to sit outside
const handlePadding = 12; // pixels

// valid size restriction formats are
//
//   size=[<width>,<height>] - aspect ratio > 1 -> limited by maximum width
//                           - aspect ratio < 1 -> limited by maximum height
//   size=[<width>]          - maximum height = aspect ratio * width
const sizeRegEx = /size=\[\d+(,\d+)?\]/i;

export default class ImageWidget extends Component {
  static propTypes = {
    alt: PropTypes.string,
    attr: PropTypes.shape({
      size: PropTypes.string,
    }),
    src: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
  };

  static defaultProps = {
    alt: placeholder.text,
  };

  constructor(props) {
    super(props);

    const {
      attr: { size: origSize },
      src,
    } = this.props;
    this.state = {
      aspectRatio: 1,
      minConstraints: [0, 0],
      maxConstraints: [0, 0],
      origSize, // remember initial size restriction
      size: {
        width: 0,
        height: 0,
      },
      src, // <img src=...> should only be changed by Asset Explorer
      value: 0,
      valueIsInvalid: false,
    };
  }

  onClick = () => {
    const { attr, onAssetsOpen } = this.props;
    const { origSize: size } = this.state;

    onAssetsOpen(
      {
        ...attr,
        size, // always open Asset Explorer with initial size restriction
      },
      (asset) => {
        const { attr, uuid: assetId, download: src } = asset;

        this.setState({ src });
        this.onUpdate(src, {
          ...attr,
          assetId,
        });
      }
    );
  };

  onInputChange = (event) => {
    const {
      aspectRatio,
      minConstraints: [minWidth],
      maxConstraints: [maxWidth],
    } = this.state;
    const value = +(+event.target.value).toFixed(); // convert to integer
    const valueIsInvalid = value < minWidth || value > maxWidth;

    this.setState({
      value,
      valueIsInvalid,
      ...(valueIsInvalid
        ? undefined // don't touch size for invalid value
        : {
          size: {
            width: value,
            height: value * aspectRatio,
          },
        }),
    }, () => {
      // handle manual update like end of resize
      if (!valueIsInvalid) this.onResizeStop();
    });
  };

  onLoad = () => {
    let { naturalHeight: height, naturalWidth: width } = this._image;
    const aspectRatio = height / width;
    const minImageHeight = minImageWidth * aspectRatio;

    // Always allow resizing up to the maximum, regardless of current image size
    // The current image might be smaller due to size constraints used in asset selection,
    // but the user should be able to resize it larger up to the maximum allowed
    const maxWidth = maxImageWidth;
    const maxHeight = maxImageWidth * aspectRatio;

    // Use current image size as starting point, but don't let it limit maximum constraints
    let displayWidth = width;
    let displayHeight = height;
    
    // If current image is larger than max, limit it
    if (displayWidth > maxImageWidth) {
      displayWidth = maxImageWidth;
      displayHeight = displayWidth * aspectRatio;
    }

    // initialize constraints from loaded image
    // Add padding to constraints for ResizableBox (handles need space outside)
    this.setState({
      aspectRatio,
      minConstraints: [minImageWidth + handlePadding, minImageHeight + handlePadding],
      maxConstraints: [maxWidth + handlePadding, maxHeight + handlePadding],
      size: {
        width: displayWidth,
        height: displayHeight,
      },
      value: displayWidth,
      valueIsInvalid: false,
    });
  };

  onResize = (_, { size }) => {
    this.setState({
      size: {
        width: size.width - handlePadding,
        height: size.height - handlePadding,
      },
      value: +(size.width - handlePadding).toFixed(), // convert to integer
      valueIsInvalid: false,
    });
  };

  onResizeStop = () => {
    const { attr } = this.props;
    const {
      size: { height, width },
      src,
    } = this.state;
    // asset API only accepts integers
    const parsedWidth = parseFloat(width);
    const parsedHeight = parseFloat(height);
    const size = `[${parsedWidth.toFixed()},${parsedHeight.toFixed()}]`;

    this.onUpdate(
      src.match(sizeRegEx)
        ? src.replace(sizeRegEx, `size=${size}`)
        : `${src.trimEnd()}&size=${size}`,
      {
        ...attr,
        size,
      }
    );
  };

  onUpdate(src, attr) {
    const { alt, onHandleContent, uuid } = this.props;

    Object.assign(attr, { _type: "ao1" });
    onHandleContent({
      alt,
      attr,
      src,
      uuid,
    });
  }

  render() {
    const { alt, attr } = this.props;
    const { minConstraints, maxConstraints, size, src, value, valueIsInvalid } =
      this.state;

    // Add padding to ResizableBox dimensions so handles sit outside the image
    const boxSize = {
      width: size.width + handlePadding,
      height: size.height + handlePadding,
    };

    return (
      <div className="rule-builder__img">
        <ResizableBox
          lockAspectRatio
          minConstraints={minConstraints}
          maxConstraints={maxConstraints}
          onResize={this.onResize}
          onResizeStop={this.onResizeStop}
          resizeHandles={["n", "nw", "ne", "w", "e", "s", "sw", "se"]}
          {...boxSize}
        >
          <Fragment>
            <img
              alt={alt}
              onClick={this.onClick}
              onLoad={this.onLoad}
              ref={(ref) => (this._image = ref)}
              src={src}
              width={size.width}
              height={size.height}
            />
            <div className="rule-builder__img_inputs">
              <div className="label">Width</div>
              <Input
                className={`input input_filter ${
                  valueIsInvalid ? "input_warning" : ""
                }`}
                onChange={this.onInputChange}
                type="number"
                value={value}
              />
            </div>
          </Fragment>
        </ResizableBox>
      </div>
    );
  }
}